
import React, { useState, useEffect } from 'react';
import { Authority } from '../../types';
import { fetchAuthorities, createAuthority, updateAuthority, deleteAuthority } from '../../services/cloudService';
import { Button } from '../Button';
import { READ_ONLY_AUSWEG, READ_ONLY_HINWEIS, readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { Search, Plus, MapPin, Trash2, Edit2, Save, X, Landmark, Loader2, Phone, Printer, Eye } from 'lucide-react';

interface Props {
    /** Lesemodus des Projekts, aus dem diese Ansicht geoeffnet wurde.
     *
     *  Die Behoerdenliste ist GLOBAL — ein Anlegen/Aendern/Loeschen hier waere
     *  kein Projekt-Write, aber es waere dauerhaft und wuerde alle Projekte
     *  treffen, waehrend der Balken darueber „Eingaben werden nicht
     *  gespeichert" verspricht. Der Widerspruch wird hier aufgeloest: lesen
     *  und suchen bleibt, schreiben ist gesperrt. */
    isReadOnly?: boolean;
}

export const AuthorityManager: React.FC<Props> = ({ isReadOnly = false }) => {
    const [authorities, setAuthorities] = useState<Authority[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    
    // Form State
    const [formData, setFormData] = useState<Omit<Authority, 'id'>>({
        name: '',
        address: '',
        email: '',
        phone: '',
        fax: '',
        catchment_area: ''
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchAuthorities();
            setAuthorities(data);
        } catch (e) {
            console.error(e);
            alert("Fehler beim Laden der Behörden.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const filteredAuthorities = authorities.filter(a => 
        a.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (a.catchment_area || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.address.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Zweiter Zaun hinter den ausgegrauten Knoepfen: kein stiller Abbruch,
    // sondern eine Absage mit Grund und Ausweg.
    const handleEdit = (auth: Authority) => {
        if (isReadOnly) {
            alert(readOnlyMessage('Die Behörde wurde nicht geändert.'));
            return;
        }
        setEditId(auth.id);
        setFormData({
            name: auth.name,
            address: auth.address,
            email: auth.email || '',
            phone: auth.phone || '',
            fax: auth.fax || '',
            catchment_area: auth.catchment_area || ''
        });
        setIsEditing(true);
    };

    const handleDelete = async (id: string) => {
        if (isReadOnly) {
            alert(readOnlyMessage('Die Behörde wurde nicht gelöscht.'));
            return;
        }
        if (!confirm("Behörde wirklich löschen?")) return;
        try {
            await deleteAuthority(id);
            await loadData();
        } catch {
            alert("Fehler beim Löschen.");
        }
    };

    const handleSave = async () => {
        if (isReadOnly) {
            alert(readOnlyMessage('Die Behörde wurde weder angelegt noch geändert.'));
            return;
        }
        if (!formData.name || !formData.address) {
            alert("Name und Anschrift sind Pflichtfelder.");
            return;
        }

        try {
            if (editId) {
                await updateAuthority(editId, formData);
            } else {
                await createAuthority(formData);
            }
            setIsEditing(false);
            setEditId(null);
            setFormData({ name: '', address: '', email: '', phone: '', fax: '', catchment_area: '' });
            await loadData();
        } catch {
            alert("Fehler beim Speichern.");
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditId(null);
        setFormData({ name: '', address: '', email: '', phone: '', fax: '', catchment_area: '' });
    };

    return (
        <div data-glass-view="module-vorank-behoerden" data-glass-bereich="module" className="h-full flex flex-col bg-slate-50 animate-fade-in relative">
            <div data-glass="kopf" className="p-6 border-b border-slate-200 bg-white shadow-sm flex justify-between items-center shrink-0">
                <div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Landmark className="w-6 h-6 text-blue-600" />
                        Behörden & Ämter
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">Verwaltung der Adressen für Vorankündigungen.</p>
                </div>
                {!isEditing && (
                    <Button
                        variant="primary"
                        onClick={() => setIsEditing(true)}
                        disabled={isReadOnly}
                        icon={<Plus className="w-4 h-4" />}
                        title={isReadOnly ? readOnlyTitle('Es wird keine Behörde angelegt.') : undefined}
                    >
                        Neue Behörde
                    </Button>
                )}
            </div>

            {/* Der Balken ueber der Ansicht sagt „Eingaben werden nicht
                gespeichert" — fuer die globale Behoerdenliste stimmte das
                nicht. Statt den Widerspruch zu erklaeren, wird er hier
                beseitigt: die Schreibwege sind zu und sagen warum. */}
            {isReadOnly && (
                <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs font-bold flex items-start gap-2 shrink-0">
                    <Eye className="w-4 h-4 shrink-0 mt-px" />
                    <span>
                        {READ_ONLY_HINWEIS} Behörden gelten projektübergreifend und lassen sich hier
                        deshalb weder anlegen noch ändern oder löschen — Suchen und Nachschlagen bleibt
                        möglich. {READ_ONLY_AUSWEG}
                    </span>
                </div>
            )}

            <div className="flex-1 overflow-hidden flex">
                {/* List View */}
                <div className={`flex-1 flex flex-col ${isEditing ? 'hidden md:flex md:w-1/2 border-r border-slate-200' : 'w-full'}`}>
                    <div className="p-4 border-b border-slate-200 bg-white">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input 
                                type="text" 
                                placeholder="Suchen nach Name, Ort oder Zuständigkeit..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                        {loading ? (
                            <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
                        ) : filteredAuthorities.length === 0 ? (
                            <div className="text-center p-8 text-slate-400 italic">Keine Behörden gefunden.</div>
                        ) : (
                            filteredAuthorities.map(auth => (
                                <div key={auth.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-slate-800">{auth.name}</h3>
                                        <div className="flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => handleEdit(auth)}
                                                disabled={isReadOnly}
                                                title={isReadOnly ? readOnlyTitle('Die Behörde wird nicht geändert.') : 'Bearbeiten'}
                                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:bg-transparent"
                                            ><Edit2 className="w-4 h-4"/></button>
                                            <button
                                                onClick={() => handleDelete(auth.id)}
                                                disabled={isReadOnly}
                                                title={isReadOnly ? readOnlyTitle('Die Behörde wird nicht gelöscht.') : 'Löschen'}
                                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:bg-transparent"
                                            ><Trash2 className="w-4 h-4"/></button>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-2 text-sm text-slate-600 mb-2">
                                        <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
                                        <span className="whitespace-pre-line">{auth.address}</span>
                                    </div>
                                    
                                    {(auth.phone || auth.fax) && (
                                        <div className="flex gap-4 mb-2 text-xs text-slate-500">
                                            {auth.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3"/> {auth.phone}</div>}
                                            {auth.fax && <div className="flex items-center gap-1"><Printer className="w-3 h-3"/> {auth.fax}</div>}
                                        </div>
                                    )}

                                    {auth.catchment_area && (
                                        <div className="mt-2 pt-2 border-t border-slate-100">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Zuständig für:</span>
                                            <p className="text-xs text-slate-600 mt-1">{auth.catchment_area}</p>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Edit Form */}
                {isEditing && (
                    <div data-glass="ueberlage" className="flex-1 md:w-1/2 bg-slate-50 p-6 flex flex-col overflow-y-auto animate-in slide-in-from-right-4 duration-300 absolute md:relative inset-0 z-20 md:z-0">
                        <div data-glass="karte" className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200 max-w-lg mx-auto w-full">
                            <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                                <h3 className="font-bold text-lg text-slate-800">{editId ? 'Behörde bearbeiten' : 'Neue Behörde anlegen'}</h3>
                                <button onClick={handleCancel} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-5 h-5"/></button>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Name der Behörde *</label>
                                    <input 
                                        type="text" 
                                        value={formData.name} 
                                        onChange={e => setFormData({...formData, name: e.target.value})}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="z.B. Gewerbeaufsichtsamt..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Anschrift *</label>
                                    <textarea 
                                        rows={4}
                                        value={formData.address} 
                                        onChange={e => setFormData({...formData, address: e.target.value})}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                        placeholder="Straße, PLZ, Ort"
                                    />
                                </div>
                                
                                {/* Kontaktblock */}
                                <div className="space-y-4 pt-2 border-t border-slate-100">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Kontaktdaten</h4>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">E-Mail</label>
                                        <input 
                                            type="email" 
                                            value={formData.email} 
                                            onChange={e => setFormData({...formData, email: e.target.value})}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="kontakt@amt.de"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Telefon</label>
                                        <input 
                                            type="text" 
                                            value={formData.phone} 
                                            onChange={e => setFormData({...formData, phone: e.target.value})}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="040 123456"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fax</label>
                                        <input 
                                            type="text" 
                                            value={formData.fax} 
                                            onChange={e => setFormData({...formData, fax: e.target.value})}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="040 123456-9"
                                        />
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-100">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Zuständigkeitsbereich (Stichworte)</label>
                                    <input 
                                        type="text" 
                                        value={formData.catchment_area} 
                                        onChange={e => setFormData({...formData, catchment_area: e.target.value})}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="z.B. Hamburg, Altona, Tiefbau, 20xxx"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1">Hilft bei der Suche im Formular.</p>
                                </div>
                            </div>

                            <div className="mt-8 flex gap-3 justify-end">
                                <Button variant="ghost" onClick={handleCancel}>Abbrechen</Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSave}
                                    disabled={isReadOnly}
                                    icon={<Save className="w-4 h-4"/>}
                                    title={isReadOnly ? readOnlyTitle('Die Behörde wird nicht gespeichert.') : undefined}
                                >Speichern</Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
